/**
 * LetterGenerator — the main letter grid page for executors.
 * Shows all available letter instances grouped by category.
 * Accessed from the ExecutorPostDeathDashboard.
 */

import { useState, useId } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Building2,
  Shield,
  Landmark,
  MapPin,
  Briefcase,
  Zap,
  FileText,
  ChevronRight,
  Download,
  CheckCircle2,
  Clock,
  RefreshCw,
  FileDown,
  Info,
  ChevronDown,
  ChevronUp,
  User,
  Save,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

interface LetterInstance {
  instanceKey: string;
  templateId: string;
  templateType: string;
  templateName: string;
  category: string;
  displayName: string;
  institutionName?: string;
  accountIdentifier?: string;
  mergedData: Record<string, string>;
  executorFields: Array<{
    key: string;
    label: string;
    type: string;
    options?: string[];
    required: boolean;
    defaultValue?: string;
  }>;
  generatedLetterId?: string;
  status: "not_generated" | "draft" | "generated" | "downloaded";
  generatedAt?: string | null;
  downloadedAt?: string | null;
  pdfUrl?: string | null;
}

const CATEGORY_META: Record<string, { label: string; icon: React.FC<any>; color: string }> = {
  financial:             { label: "Financial Institutions", icon: Building2,  color: "text-blue-600"   },
  insurance:             { label: "Insurance",              icon: Shield,      color: "text-green-600"  },
  government_federal:    { label: "Government — Federal",   icon: Landmark,    color: "text-purple-600" },
  government_provincial: { label: "Government — Ontario",   icon: MapPin,      color: "text-orange-600" },
  employment:            { label: "Employment & Pensions",  icon: Briefcase,   color: "text-yellow-600" },
  utilities:             { label: "Utilities & Services",   icon: Zap,         color: "text-teal-600"   },
  other:                 { label: "Other Notifications",    icon: FileText,    color: "text-gray-600"   },
};

const CATEGORY_ORDER = ["financial", "insurance", "government_federal", "government_provincial", "employment", "utilities", "other"];

interface Props {
  clientId: string;
  /** When true, shows a read-only preview banner (planner viewing their own executor tools) */
  previewMode?: boolean;
}

interface ExecutorProfile {
  name: string;
  relationship?: string | null;
  email: string;
  phone?: string | null;
  executorAddress?: string | null;
  deathCertificateNumber?: string | null;
}

export default function LetterGenerator({ clientId, previewMode = false }: Props) {
  const uid = useId();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileEdits, setProfileEdits] = useState<{ executorAddress: string; deathCertificateNumber: string }>({ executorAddress: "", deathCertificateNumber: "" });
  const [profileSaved, setProfileSaved] = useState(false);

  const { data: profile, isSuccess: profileLoaded } = useQuery<ExecutorProfile>({
    queryKey: ["/api/clients", clientId, "my-executor-profile"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/my-executor-profile`);
      if (!res.ok) throw new Error("No profile");
      return res.json();
    },
    enabled: !!clientId && !previewMode,
    retry: false,
  });

  // Sync profile data into editable state once loaded
  const [profileInitialized, setProfileInitialized] = useState(false);
  if (profileLoaded && profile && !profileInitialized) {
    setProfileInitialized(true);
    setProfileEdits({
      executorAddress: profile.executorAddress ?? "",
      deathCertificateNumber: profile.deathCertificateNumber ?? "",
    });
  }

  const saveProfileMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/my-executor-profile`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profileEdits),
      });
      if (!res.ok) throw new Error("Failed to save");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "my-executor-profile"] });
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2000);
    },
    onError: (err: any) => {
      toast({ title: "Save failed", description: err.message, variant: "destructive" });
    },
  });

  const { data: letters = [], isLoading } = useQuery<LetterInstance[]>({
    queryKey: ["/api/clients", clientId, "letters"],
    queryFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/letters`);
      if (!res.ok) throw new Error("Failed to fetch letters");
      return res.json();
    },
    enabled: !!clientId,
  });

  const generateAllMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/clients/${clientId}/letters/generate-all`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Generation failed");
      }
      const count = res.headers.get("X-Letters-Count");
      const skipped = res.headers.get("X-Letters-Skipped");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "estate-notification-letters.pdf";
      a.click();
      URL.revokeObjectURL(url);
      return { count: parseInt(count ?? "0"), skipped: parseInt(skipped ?? "0") };
    },
    onSuccess: ({ count, skipped }) => {
      toast({
        title: "Letters downloaded",
        description: `${count} letter${count !== 1 ? "s" : ""} generated.${skipped > 0 ? ` ${skipped} skipped due to missing info.` : ""}`,
      });
    },
    onError: (err: any) => {
      toast({ title: "Failed to generate", description: err.message, variant: "destructive" });
    },
  });

  // Group letters by category
  const grouped = letters.reduce<Record<string, LetterInstance[]>>((acc, l) => {
    if (!acc[l.category]) acc[l.category] = [];
    acc[l.category].push(l);
    return acc;
  }, {});

  const totalCount = letters.length;
  const generatedCount = letters.filter(l => l.status === "generated" || l.status === "downloaded").length;
  const generatedPct = totalCount > 0 ? Math.round((generatedCount / totalCount) * 100) : 0;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-muted-foreground">Loading letter templates…</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Preview mode banner */}
      {previewMode && (
        <div className="flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
          <Info className="w-4 h-4 mt-0.5 shrink-0 text-blue-600" />
          <div>
            <p className="font-medium">Preview Mode</p>
            <p className="text-blue-700 mt-0.5">This is a preview of what your executor will see. Letters cannot be generated until a death event is verified.</p>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Letter Generator</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Pre-filled notification letters to accompany the death certificate.
          </p>
        </div>
        <Button
          onClick={() => generateAllMutation.mutate()}
          disabled={generateAllMutation.isPending || totalCount === 0 || previewMode}
          className="shrink-0"
        >
          {generateAllMutation.isPending ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Generating…
            </>
          ) : (
            <>
              <FileDown className="w-4 h-4 mr-2" />
              Download All ({totalCount})
            </>
          )}
        </Button>
      </div>

      {/* Progress */}
      {totalCount > 0 && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="font-medium">Letters generated</span>
              <span className="text-muted-foreground">{generatedCount} / {totalCount}</span>
            </div>
            <Progress value={generatedPct} className="h-2" />
            <p className="text-xs text-muted-foreground mt-2">
              Click "Generate" on each letter, review it, then download as PDF.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Executor Information card — only shown in live (non-preview) mode */}
      {!previewMode && profile && (
        <Card>
          <CardHeader
            className="pb-3 cursor-pointer select-none"
            onClick={() => setProfileOpen(o => !o)}
          >
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="w-4 h-4 text-primary" />
                Your Executor Information
                <span className="text-xs font-normal text-muted-foreground ml-1">
                  — pre-fills all letters automatically
                </span>
              </CardTitle>
              {profileOpen ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
            {!profileOpen && (
              <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                <span>{profile.name}</span>
                {profile.executorAddress && <span className="truncate max-w-[200px]">{profile.executorAddress}</span>}
                {profile.deathCertificateNumber && <span>Cert: {profile.deathCertificateNumber}</span>}
              </div>
            )}
          </CardHeader>
          {profileOpen && (
            <CardContent className="space-y-3 pt-0">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground" htmlFor={`${uid}-your-name`}>Your Name</Label>
                  <Input id={`${uid}-your-name`} value={profile.name} disabled className="h-8 text-sm bg-muted/40" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground" htmlFor={`${uid}-your-email`}>Email</Label>
                  <Input id={`${uid}-your-email`} value={profile.email} disabled className="h-8 text-sm bg-muted/40" />
                </div>
                {profile.relationship && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground" htmlFor={`${uid}-relationship`}>Relationship</Label>
                    <Input id={`${uid}-relationship`} value={profile.relationship} disabled className="h-8 text-sm bg-muted/40" />
                  </div>
                )}
                {profile.phone && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground" htmlFor={`${uid}-phone`}>Phone</Label>
                    <Input id={`${uid}-phone`} value={profile.phone} disabled className="h-8 text-sm bg-muted/40" />
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground" htmlFor={`${uid}-mailing-address`}>Your Mailing Address</Label>
                {/* F-1 (s133): multi-line so the letterhead renders a real address block. The
                    letter body already preserves line breaks (letter-pdf splits on \n; the preview
                    is whitespace-pre-wrap). Existing single-line values render unchanged. */}
                <Textarea id={`${uid}-mailing-address`}
                  value={profileEdits.executorAddress}
                  onChange={e => setProfileEdits(p => ({ ...p, executorAddress: e.target.value }))}
                  placeholder={"123 Main St\nCity, Province  A1A 1A1"}
                  rows={3}
                  className="text-sm resize-y"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground" htmlFor={`${uid}-death-certificate-number`}>Death Certificate Number</Label>
                <Input id={`${uid}-death-certificate-number`}
                  value={profileEdits.deathCertificateNumber}
                  onChange={e => setProfileEdits(p => ({ ...p, deathCertificateNumber: e.target.value }))}
                  placeholder="e.g. ON-2024-123456"
                  className="h-8 text-sm"
                />
              </div>
              <div className="flex items-center justify-between pt-1">
                <p className="text-xs text-muted-foreground">These values pre-fill all letters automatically.</p>
                <Button
                  size="sm"
                  onClick={() => saveProfileMutation.mutate()}
                  disabled={saveProfileMutation.isPending}
                  className="h-7 text-xs"
                >
                  {profileSaved ? (
                    <><CheckCircle2 className="w-3 h-3 mr-1" />Saved</>
                  ) : saveProfileMutation.isPending ? (
                    <><RefreshCw className="w-3 h-3 mr-1 animate-spin" />Saving…</>
                  ) : (
                    <><Save className="w-3 h-3 mr-1" />Save</>
                  )}
                </Button>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {totalCount === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="font-medium">No letter data available</p>
            <p className="text-sm text-muted-foreground mt-1">
              Letter templates become available once estate plan data has been entered.
              Ask your financial advisor to fill in the estate plan with account details.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Letter groups */}
      {CATEGORY_ORDER.filter(cat => grouped[cat]?.length > 0).map(cat => {
        const meta = CATEGORY_META[cat];
        const Icon = meta.icon;
        const catLetters = grouped[cat];

        return (
          <Card key={cat}>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Icon className={`w-4 h-4 ${meta.color}`} />
                {meta.label}
                <Badge variant="secondary" className="ml-auto text-xs">{catLetters.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y">
                {catLetters.map(letter => (
                  <LetterRow
                    key={letter.instanceKey}
                    letter={letter}
                    clientId={clientId}
                    onNavigate={() => setLocation(`/executor/${clientId}/letters/${encodeURIComponent(letter.instanceKey)}`)}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        );
      })}

      <p className="text-xs text-muted-foreground text-center pb-4">
        All letters should be presented alongside the original or certified copy of the death certificate and executor identification.
      </p>
    </div>
  );
}

function LetterRow({
  letter,
  clientId,
  onNavigate,
}: {
  letter: LetterInstance;
  clientId: string;
  onNavigate: () => void;
}) {
  const { toast } = useToast();
  const [downloading, setDownloading] = useState(false);

  const statusBadge = {
    not_generated: null,
    draft: <Badge variant="outline" className="text-xs"><Clock className="w-3 h-3 mr-1" />Draft</Badge>,
    generated: <Badge variant="secondary" className="text-xs bg-green-50 text-green-700 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" />Generated</Badge>,
    downloaded: <Badge variant="secondary" className="text-xs bg-green-50 text-green-700 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" />Downloaded</Badge>,
  }[letter.status];

  const handleDownload = async () => {
    if (!letter.generatedLetterId) return;
    setDownloading(true);
    try {
      const res = await fetch(`/api/clients/${clientId}/letters/${letter.generatedLetterId}/download`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = letter.displayName.replace(/[^a-z0-9]/gi, "-").toLowerCase();
      a.download = `${safeName}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      queryClient.invalidateQueries({ queryKey: ["/api/clients", clientId, "letters"] });
    } catch (err: any) {
      toast({ title: "Download failed", description: err.message, variant: "destructive" });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-3 px-4 py-3 hover:bg-muted/30 transition-colors">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{letter.displayName}</p>
        {letter.accountIdentifier && (
          <p className="text-xs text-muted-foreground">{letter.accountIdentifier}</p>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {statusBadge}
        {letter.generatedLetterId ? (
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              disabled={downloading}
              className="h-7 px-2"
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              {downloading ? "…" : "Download"}
            </Button>
            <Button variant="outline" size="sm" onClick={onNavigate} className="h-7 px-2">
              <RefreshCw className="w-3.5 h-3.5 mr-1" />
              Edit
            </Button>
          </>
        ) : (
          <Button size="sm" onClick={onNavigate} className="h-7 px-3">
            Generate
            <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        )}
      </div>
    </div>
  );
}
